import { findCountryByIso } from "@/lib/countryDialCodes";

/**
 * Multi-tenant SEED. The runtime app + Cloudflare Worker load tenants from
 * Lovable Cloud's `public.tenants` table (see useTenants() and
 * cloudflare/worker.ts). This file:
 *
 *   1. Defines the Tenant TypeScript interface used everywhere.
 *   2. Carries a small bootstrap array (the three existing cities) so cold
 *      loads, first visit, localStorage cleared, network unreachable,
 *      still render something rather than a blank tenant.
 *
 * Adding a new city is now an ADMIN UI action (Admin → Tenants → New
 * tenant). The DB row propagates to the React app on next mount and to the
 * Cloudflare Worker within ~5 minutes (edge cache TTL). No code change.
 *
 * Out of scope: per-tenant logos, color tokens, brand fonts. Each tenant
 * shares the My-Santa-Teresa-Home visual brand with the city name swapped.
 */

export interface Tenant {
  /** Internal id, used as React Query cache key segment + ?tenant= override. */
  id: string;
  /** Production hosts (and `www.` variants) that resolve to this tenant. */
  hosts: string[];
  /** This market's own GA4 property. Mirrored in index.html (GA_IDS); src/test/infraConfig.test.ts keeps the two equal. */
  gaMeasurementId?: string;
  /** "My Santa Teresa Home", used in og:site_name, Footer, etc. */
  siteName: string;
  /** "Santa Teresa", used in the hero h1 and any "Find your home in X" copy. */
  cityLabel: string;
  /** "The Nicoya Peninsula", small subtitle under the hero city name. */
  regionLabel: string;
  /** Canonical absolute base URL, used for <link rel="canonical"> + og:url. */
  siteUrl: string;
  /**
   * Display names for area chips, the listing-creation area dropdown, and
   * the fallback location-based scoping. Listings are attributed to a
   * tenant via `properties.tenant_id` (set when the admin saves);
   * `areas` is mainly UI metadata now.
   */
  areas: string[];
  /** Slug taxonomy mirror used by /areas/:area and AlertSignup defaults. */
  areaSlugs: string[];
  /** Default area selected in the AlertSignup form and the listing wizard. */
  primaryAreaSlug: string;
  /** Optional per-tenant og:image, falls back to a shared default if absent. */
  ogImage?: string;
  /**
   * ISO 3166-1 alpha-2 country code ("CR", "ES", "US", …). Used as
   * schema.org `addressCountry` and to derive a human country name via
   * `countryName(code)`. Distinct from `regionLabel` (marketing), this
   * is for structured data.
   */
  country: string;
  /**
   * Administrative region per schema.org `addressRegion`, the province
   * / state / autonomous community ("Puntarenas", "Guanacaste",
   * "Catalonia"). Distinct from `regionLabel` (hero subtitle marketing
   * copy).
   */
  addressRegion: string;
  /** Public contact email, legal pages, contact fallbacks, structured data. */
  contactEmail?: string;
  /** Social profile URLs, rendered in the footer only when present. */
  instagramUrl?: string;
  facebookUrl?: string;
  /** WhatsApp contact (E.164 number or wa.me link), optional. */
  whatsapp?: string;
  /** Postal address + geo coordinates for schema.org structured data. */
  streetAddress?: string;
  postalCode?: string;
  geoLat?: number;
  geoLng?: number;
}

export const TENANTS: Tenant[] = [
  {
    id: "santa-teresa",
    gaMeasurementId: "G-C9N73PHLHY",
    hosts: ["mysantateresahome.com", "www.mysantateresahome.com"],
    siteName: "My Santa Teresa Home",
    cityLabel: "Santa Teresa",
    regionLabel: "The Nicoya Peninsula",
    siteUrl: "https://www.mysantateresahome.com",
    areas: [
      "Santa Teresa",
      "Mal País",
      "Playa Carmen",
      "Playa Hermosa",
      "Manzanillo",
      "Cabuya",
      "Cóbano",
      "Montezuma",
      "San Isidro",
      "Santiago",
      "Las Delicias",
    ],
    areaSlugs: [
      "santa-teresa",
      "mal-pais",
      "playa-carmen",
      "playa-hermosa",
      "manzanillo",
      "cabuya",
      "cobano",
      "montezuma",
      "san-isidro",
      "santiago",
      "las-delicias",
    ],
    primaryAreaSlug: "santa-teresa",
    country: "CR",
    addressRegion: "Puntarenas",
    contactEmail: "hello@mysantateresahome.com",
    instagramUrl: "https://www.instagram.com/mysantateresahome/",
    facebookUrl: "https://www.facebook.com/profile.php?id=61590216881547",
    streetAddress: "50 m north of Plaza Kahuna",
    postalCode: "60111",
    geoLat: 9.6447,
    geoLng: -85.1696,
  },
  // Future tenants below. Add an entry, point the domain, add the host to
  // Lovable Cloud's auth allowed-redirect-URLs list, and seed at least one
  // listing whose `location` matches one of the new tenant's `areas`.
  //
  // Nosara, Guanacaste, Costa Rica. Once DNS points at this deployment,
  // the empty Browse + tenant-aware hero already renders correctly. Add
  // areas to src/data/areas.ts (with seed photos) so /areas works fully.
  {
    id: "nosara",
    gaMeasurementId: "G-XRDNX0X91H",
    hosts: ["mynosarahome.com", "www.mynosarahome.com"],
    siteName: "My Nosara Home",
    cityLabel: "Nosara",
    regionLabel: "Guanacaste",
    siteUrl: "https://www.mynosarahome.com",
    areas: ["Nosara", "Playa Guiones", "Playa Pelada", "Playa Garza", "Esperanza"],
    areaSlugs: ["nosara", "playa-guiones", "playa-pelada", "playa-garza", "esperanza"],
    primaryAreaSlug: "nosara",
    country: "CR",
    addressRegion: "Guanacaste",
    // Playa Guiones / Nosara town, where the listing map starts.
    geoLat: 9.9796,
    geoLng: -85.6533,
  },
  // Tamarindo, Guanacaste, Costa Rica. Created 2026-10-11 by
  // public.launch_market() (scripts/migrate/new-project-launch-market-2026-10-11.sql).
  {
    id: "tamarindo",
    gaMeasurementId: "G-6VPK4J26VT",
    hosts: ["mytamarindohome.com", "www.mytamarindohome.com"],
    siteName: "My Tamarindo Home",
    cityLabel: "Tamarindo",
    regionLabel: "Guanacaste",
    siteUrl: "https://www.mytamarindohome.com",
    // Angelina's list (2026-10-11), in her order; scripts/migrate/new-project-tamarindo-areas-angelina-2026-10-11.sql.
    areas: ["Playa Tamarindo", "Playa Langosta", "Playa Grande", "Villarreal", "Huacas", "Hacienda Pinilla", "Playa Avellanas", "Playa Negra", "Brasilito", "Playa Flamingo", "Potrero"],
    areaSlugs: ["tamarindo", "playa-langosta", "playa-grande", "villarreal", "huacas", "hacienda-pinilla", "playa-avellanas", "playa-negra", "brasilito", "playa-flamingo", "potrero"],
    primaryAreaSlug: "tamarindo",
    country: "CR",
    addressRegion: "Guanacaste",
    geoLat: 10.2995,
    geoLng: -85.841,
  },
  // Barcelona, different country (Spain). The shared brand still works
  // visually; the existing index.html description mentions "Costa Rica" so
  // the Cloudflare Worker (see cloudflare/) will rewrite per-host meta
  // tags. Areas are starter neighbourhoods, refine before launch.
  {
    id: "barcelona",
    hosts: ["mybarcelonahome.com", "www.mybarcelonahome.com"],
    siteName: "My Barcelona Home",
    cityLabel: "Barcelona",
    regionLabel: "Catalonia",
    siteUrl: "https://www.mybarcelonahome.com",
    areas: ["Eixample", "Gràcia", "El Born", "Barceloneta", "Sant Antoni", "Poblenou", "Gòtic", "Sarrià"],
    areaSlugs: ["eixample", "gracia", "el-born", "barceloneta", "sant-antoni", "poblenou", "gotic", "sarria"],
    primaryAreaSlug: "eixample",
    country: "ES",
    addressRegion: "Catalonia",
  },
];

/** Where maps start for a tenant: its own coordinates, else Santa Teresa's. */
export const tenantCenter = (t: Pick<Tenant, "geoLat" | "geoLng">): { lat: number; lng: number } =>
  t.geoLat != null && t.geoLng != null ? { lat: t.geoLat, lng: t.geoLng } : { lat: 9.653, lng: -85.168 };

/**
 * Human-readable country name from an ISO 3166-1 alpha-2 code. Used in
 * SEO copy and visible labels where "Costa Rica" reads better than
 * "CR". Falls back to the code itself for unmapped tenants, keeps the
 * helper from silently dropping a new country.
 */
export const countryName = (code: string): string => findCountryByIso(code)?.name ?? code;

/**
 * Resolve a tenant from a host string. Unknown hosts (localhost,
 * staging.lovable.app, branch previews) fall back to the first tenant so
 * dev / staging keeps working. The `?tenant=<id>` override on the URL takes
 * precedence, see useTenant().
 */
export const tenantForHost = (host: string): Tenant => {
  const normalized = host.toLowerCase().replace(/:\d+$/, "");
  return TENANTS.find((t) => t.hosts.includes(normalized)) ?? TENANTS[0];
};

/** The default fallback. Exported so non-React contexts (jsonLd builders,
 *  edge functions) can resolve a sensible base URL without a tenant in hand. */
export const DEFAULT_TENANT: Tenant = TENANTS[0];
